import { Link, NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useRef, useState } from 'react';
import { gsap, useGSAP } from '../lib/gsap';
import { isIOS } from '../lib/platform';
import type { ReactNode } from 'react';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function MobileMenu({ open, onClose, children }: MobileMenuProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // iOS 上动画期间临时关闭全站毛玻璃(见 .zx-suspend-glass)，避免位移逐帧重算模糊掉帧。
  const [ios] = useState(isIOS);

  // 下拉入场：遮罩淡入 + 面板从顶部下滑展开；reduce-motion 下跳过(直接到位)。
  useGSAP(
    () => {
      if (!open) return;
      const panel = panelRef.current;
      if (!panel) return;
      // iOS WebKit 屏幕上只要有 backdrop-filter 元素，任何动画都逐帧重算它→掉帧。
      // 故展开期间临时挂 html.zx-suspend-glass 关掉全站毛玻璃，落位/清理时移除。
      const root = document.documentElement;
      const suspendGlass = () => { if (ios) root.classList.add('zx-suspend-glass'); };
      const resumeGlass = () => { if (ios) root.classList.remove('zx-suspend-glass'); };
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        suspendGlass();
        if (backdropRef.current) {
          gsap.fromTo(backdropRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2, ease: 'power1.out' });
        }
        gsap.fromTo(
          panel,
          { autoAlpha: 0, y: -12 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.28,
            ease: 'power3.out',
            force3D: true,
            onComplete: () => {
              resumeGlass();
              gsap.set(panel, { clearProps: 'transform' });
            },
          },
        );
      });
      return () => {
        mm.revert();
        resumeGlass(); // 动画途中关闭也要恢复毛玻璃，避免类残留
      };
    },
    { dependencies: [open] },
  );

  if (!open) return null;
  // 通过 portal 渲染到 body：Header 的 backdrop-blur 会成为 fixed 定位的包含块，
  // 若不脱离，fixed inset-0 只会铺满 header，导致遮罩/面板定位失真。
  return createPortal(
    <div className="fixed inset-0 z-[60] lg:hidden">
      {/* 遮罩调淡：让有层次的页面透上来，磨砂才显形；点击关闭 */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-[rgba(2,6,23,0.3)]"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* 下拉面板：贴在悬浮胶囊下方(top-[64px])，留左右边距 + 圆角 + 向下展开。
          iOS 用实色底(glass-panel-solid)，避免「先透明再模糊」的割裂过渡；非 iOS 用真毛玻璃。 */}
      <div
        ref={panelRef}
        className={`${ios ? 'glass-panel-solid' : 'liquid-glass-panel'} absolute left-3 right-3 top-[64px] max-h-[calc(100dvh-80px)] overflow-y-auto rounded-card border border-line p-2`}
      >
        <nav className="flex flex-col gap-1 text-sm">{children}</nav>
      </div>
    </div>,
    document.body,
  );
}

export function MobileMenuLink({
  to,
  onClick,
  icon,
  children,
}: {
  to: string;
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
}) {
  // 全宽行：清晰描边 + 可选左侧图标（比原来纯底色更易识别「块边」）。不依赖 NavLink 的 isActive：
  // 几个分类链接 query 不同但 pathname 同为 /datasets，会被一起判为命中导致高亮不一致，故铺平。
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-control border border-line bg-surface px-3 py-2.5 text-fg transition-colors hover:border-navy-line hover:bg-surface-muted"
    >
      {icon && (
        <span className="shrink-0 text-navy" aria-hidden>
          {icon}
        </span>
      )}
      <span>{children}</span>
    </NavLink>
  );
}

/**
 * 图标磁贴：大图标在上、文字在下、清晰描边——更易识别。用于菜单里的分类/账户入口网格。
 * 有 to 则为链接；无 to 则为按钮（登录/注册等动作）。tone='solid' 为强调实心（注册）。
 */
export function MenuTile({
  to,
  onClick,
  icon,
  label,
  tone = 'default',
}: {
  to?: string;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  tone?: 'default' | 'solid';
}) {
  const cls = `flex flex-col items-center justify-center gap-1.5 rounded-control border px-2 py-3 text-center transition-colors ${
    tone === 'solid'
      ? 'border-navy-fill bg-navy-fill text-white hover:bg-navy-fill-strong'
      : 'border-line bg-surface text-fg hover:border-navy-line hover:bg-surface-muted'
  }`;
  const inner = (
    <>
      <span className={tone === 'solid' ? '' : 'text-navy'} aria-hidden>
        {icon}
      </span>
      <span className="text-xs font-medium leading-tight">{label}</span>
    </>
  );
  if (to) {
    return (
      <NavLink to={to} onClick={onClick} className={cls}>
        {inner}
      </NavLink>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

export function MobileMenuButton({
  to,
  onClick,
  variant,
  children,
}: {
  to?: string;
  onClick: () => void;
  variant: 'ghost' | 'solid';
  children: ReactNode;
}) {
  const className = `mx-3 my-1 block rounded-control px-3 py-2 text-center ${
    variant === 'solid'
      ? 'bg-navy-fill text-white hover:bg-navy-fill-strong'
      : 'border border-line text-fg-muted hover:bg-canvas'
  }`;
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${className} w-[calc(100%-1.5rem)]`}>
      {children}
    </button>
  );
}
